/**
 * What the two onboarding surfaces SAY (DECISIONS D-013).
 *
 * Same convention as the rest of src/ui/state: anything that decides an outcome
 * or picks a sentence is a pure function here, tested in the node environment
 * the unit suite runs in, so the `.tsx` files stay wiring.
 *
 * Two audiences, and their needs are opposite:
 *
 *   The OFFICE is looking at a code on a screen and needs to know how long it
 *   is good for and that showing a new one kills the old one.
 *   The SURVEYOR is standing there holding a phone that either worked or did
 *   not, and needs to know which — and, if it did not, whose problem it is and
 *   what unsticks it. "Setup failed" is the one thing that must never appear.
 */

import { ONBOARDING_TTL_HOURS } from '../../core/onboarding';

/* ══════════════════════════ the office's side ═══════════════════════════ */

/**
 * How long the code on screen has left, in words.
 *
 * Relative rather than absolute because relative is what the office is actually
 * deciding with — "is this still good?" — and because an absolute time needs a
 * timezone the browser has and the test does not. The screen prints the wall
 * clock beside this, from the viewer's own locale.
 */
export function describeOnboardingWindow(expiresAtIso: string, nowIso: string): string {
  const expires = Date.parse(expiresAtIso);
  const now = Date.parse(nowIso);
  if (Number.isNaN(expires) || Number.isNaN(now)) {
    return `This code works once and expires ${String(ONBOARDING_TTL_HOURS)} hours after it was made.`;
  }

  const minutes = Math.floor((expires - now) / 60_000);
  if (minutes <= 0) return 'This code has expired. Make a new one.';
  if (minutes < 60) {
    return `This code works once, and expires in ${String(minutes)} minute${minutes === 1 ? '' : 's'}.`;
  }

  const hours = Math.round(minutes / 60);
  return `This code works once, and expires in ${String(hours)} hour${hours === 1 ? '' : 's'}.`;
}

/**
 * `BCD-FGH-JKL` → `B C D F G H J K L`, for an accessible name (D-019).
 *
 * A screen reader given the raw code attacks it as a word — "BCD-FGH-JKL" comes
 * out as a swallowed mumble, and the office member reading it to a surveyor
 * over the phone gets nothing usable. Spaced-out letters are announced one at a
 * time, which is how a code has to be heard.
 *
 * Display only. Never hashed, never sent — the value that travels is what the
 * surveyor types, normalised by `normalizePairCode`.
 */
export function spellOut(code: string): string {
  return code
    .split('')
    .filter((character) => /[A-Za-z0-9]/.test(character))
    .join(' ');
}

/** What the office is told when a code is printed, including what it killed. */
export function onboardingIssuedMessage(input: {
  surveyorName: string;
  invalidatedPriorLinks: number;
}): string {
  const base = `Scan this with ${input.surveyorName}’s phone to finish setting them up.`;
  return input.invalidatedPriorLinks > 0
    ? `${base} Any code you showed them before has stopped working.`
    : base;
}

/* ═════════════════════════ the surveyor's side ══════════════════════════ */

export type OnboardPhase = 'redeeming' | 'linked' | 'refused' | 'offline' | 'nothing';

export interface OnboardView {
  phase: OnboardPhase;
  headline: string;
  detail: string;
  /** True when the surveyor can get on with their work from here. */
  canProceed: boolean;
}

/**
 * The screen a scanned code lands on.
 *
 * `message` is the API's own sentence where there was one — those are written
 * for this screen (see the refusal table in functions/api/[[route]].ts) and are
 * more specific than anything that could be reconstructed from a code here.
 * The fallbacks exist so a code this build has never seen still produces a
 * sentence rather than an identifier.
 */
export function onboardView(input: {
  phase: OnboardPhase;
  surveyorName?: string;
  errorCode?: string;
  errorMessage?: string;
}): OnboardView {
  switch (input.phase) {
    case 'redeeming':
      return {
        phase: 'redeeming',
        headline: 'Setting up this phone…',
        detail: 'One moment. This only takes a second.',
        canProceed: false,
      };

    case 'linked':
      return {
        phase: 'linked',
        headline: input.surveyorName
          ? `You’re set, ${firstWord(input.surveyorName)}.`
          : 'You’re set.',
        detail:
          'This phone is linked to you now. It keeps working with no signal — everything ' +
          'you capture saves here and uploads on its own.',
        canProceed: true,
      };

    case 'offline':
      return {
        phase: 'offline',
        headline: 'Can’t reach the office.',
        detail:
          'Nothing has been used up — the code on the office screen still works. Get a bar ' +
          'of signal and scan it again.',
        canProceed: false,
      };

    case 'refused':
      return {
        phase: 'refused',
        headline: refusalHeadline(input.errorCode),
        detail:
          input.errorMessage && input.errorMessage.length > 0
            ? input.errorMessage
            : 'Ask the office to show you a new setup code.',
        canProceed: false,
      };

    case 'nothing':
      return {
        phase: 'nothing',
        headline: 'Nothing to set up here.',
        detail:
          'Point your camera at the setup code on the office screen and open the link it ' +
          'gives you.',
        canProceed: false,
      };
  }
}

/**
 * The headline names WHOSE problem it is, because that is the actionable part.
 * A code that was already used is not the surveyor doing something wrong, and a
 * screen that reads like an error tells them it was.
 */
function refusalHeadline(code: string | undefined): string {
  switch (code) {
    case 'onboarding_link_used':
      return 'That code has already been used.';
    case 'onboarding_link_expired':
      return 'That code has expired.';
    case 'onboarding_link_replaced':
      return 'That code was replaced by a newer one.';
    case 'surveyor_inactive':
      return 'That account is switched off.';
    case 'onboarding_link_unknown':
      return 'That code wasn’t recognised.';
    default:
      return 'This phone could not be set up.';
  }
}

/** "Dave Reyes" → "Dave". A greeting, not a record. */
export function firstWord(name: string): string {
  return name.trim().split(/\s+/)[0] ?? name;
}
